import { useToggleFavorite } from "@/api/resourceTags";
import { useDeleteSkill, usePutSkill, useSkills } from "@/api/skills";
import { SkillUploadDialog } from "@/components/SkillUploadDialog/SkillUploadDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import { SkillsIndexPageInner } from "@/pages/skills/SkillsIndexPageInner";
import { route } from "@/utils/route";
import { RESOURCE_TAG_FAVORITE, Skill } from "@cohesive/api-client";
import {
  IndexLayout,
  IndexLayoutHeader,
  IndexLayoutHeaderAction,
  IndexLayoutHeaderDescription,
  IndexLayoutHeaderIcon,
  IndexLayoutHeaderTitle,
  IndexLayoutStickyHeader,
} from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { FileArchive, PenLine, Plus, ScrollText } from "lucide-react";
import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export const SkillsIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);
  const queryClient = useQueryClient();
  const appNavigate = useAppNavigate();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  /** Defer open so DropdownMenu can finish unmounting; avoids body stuck with pointer-events: none (Radix #3317). */
  const openUploadZipDialog = useCallback(() => {
    window.setTimeout(() => setUploadDialogOpen(true), 0);
  }, []);

  const { data: skills = [], isLoading, error } = useSkills();

  const { mutate: updateSkill } = usePutSkill({
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success(
        `Skill ${data.status === "enabled" ? "enabled" : "disabled"}`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update skill");
    },
  });

  const { mutate: deleteSkill } = useDeleteSkill({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast.success("Skill deleted successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete skill");
    },
  });

  const toggleFavorite = useToggleFavorite();

  const handleConfirmDeleteSkill = async (skill: Skill) => {
    deleteSkill(skill.id);
  };

  const handleSkillAction = (skill: Skill, action: string) => {
    switch (action) {
      case "favorite":
        toggleFavorite.mutate({
          resourceType: "skill",
          resourceId: skill.id,
          isFavorite: !!skill.tags?.includes(RESOURCE_TAG_FAVORITE),
        });
        break;
      case "view":
        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SKILLS.SHOW, { id: skill.id }),
        });
        break;
      case "enable":
        updateSkill({
          id: skill.id,
          data: { status: "enabled" },
        });
        break;
      case "disable":
        updateSkill({
          id: skill.id,
          data: { status: "disabled" },
        });
        break;
      case "delete":
        setSkillToDelete(skill);
        break;
      default:
        console.log(`Performing ${action} on skill ${skill.id}`);
    }
  };

  const handleSkillClick = (skill: Skill) => {
    appNavigate({
      pane: "rightPane",
      to: route(ROUTES.SKILLS.SHOW, { id: skill.id }),
    });
  };

  const showZeroState = skills.length === 0 && !isLoading && !error;

  return (
    <>
      <title>Skills</title>
      <IndexLayout className="relative pb-8">
        <IndexLayoutStickyHeader>
          <IndexLayoutHeader>
            <IndexLayoutHeaderIcon>
              <ScrollText />
            </IndexLayoutHeaderIcon>
            <IndexLayoutHeaderTitle>Skills</IndexLayoutHeaderTitle>
            <IndexLayoutHeaderDescription>
              Manage and organize your Agent's skills
            </IndexLayoutHeaderDescription>
            <IndexLayoutHeaderAction>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        className="h-12 w-12 rounded-full opacity-80 hover:opacity-100 hover:scale-110 shadow-sm hover:shadow-lg transition-all duration-300"
                        aria-label="Create Skill"
                      >
                        <Plus className="!h-5 !w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Create Skill</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(ROUTES.SKILLS.NEW)}>
                    <PenLine className="h-4 w-4 mr-2" />
                    Create from scratch
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={openUploadZipDialog}>
                    <FileArchive className="h-4 w-4 mr-2" />
                    Upload a skill
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </IndexLayoutHeaderAction>
          </IndexLayoutHeader>
        </IndexLayoutStickyHeader>
        <SkillsIndexPageInner
          viewMode="page"
          isLoading={isLoading}
          error={error}
          showZeroState={showZeroState}
          skills={skills}
          handleSkillAction={handleSkillAction}
          handleSkillClick={handleSkillClick}
          skillToDelete={skillToDelete}
          handleConfirmDeleteSkill={handleConfirmDeleteSkill}
          setSkillToDelete={setSkillToDelete}
          onCreateSkill={() => navigate(ROUTES.SKILLS.NEW)}
          onUploadZip={openUploadZipDialog}
        />
      </IndexLayout>
      <SkillUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
    </>
  );
};

export default SkillsIndexPage;
